import { useEffect, useMemo, useState } from "react";

import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useFieldArray, useForm } from "react-hook-form";

import { CameraIcon } from "@heroicons/react/24/outline";

import Modal from "../../layouts/Modal";
import useLeads from "../../data/useLeads";
import { IContact } from "../../models/Contact";
import { IVehicle } from "../../models/Vehicle";
import Card from "../../components/shared/Card/Card";
import { ILead, LeadSource } from "../../models/Lead";
import { DefaultResponseWithData } from "../../models/system";
import LoadingWheel from "../../components/shared/LoadingWheel";
import useVehiclesMutations from "../../data/useVehiclesMutations";
import LeadVehiclesTable from "../../components/vehicles/VehicleRow";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import SelectInput from "../../components/shared/Inputs/SelectInput";
import { AccountType, IAccount } from "../../models/account/IAccount";
import ContactSelector from "../../components/contacts/ContactSelector";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import GetAddresses from "../../components/shared/Addresses/GetAddresses";
import Breadcrumbs from "../../components/shared/Breadcrumbs/Breadcrumbs";
import useNotification from "../../components/notifications/useNotifications";

export default function CreateLeadMultipleVehicles() {
  const { t } = useTranslation("leads");
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isBookingPath = pathname.startsWith("/bookings");
  const leadLabel = isBookingPath ? "Booking" : "Lead";
  const leadListLabel = isBookingPath ? "Bookings" : "Leads";
  const { addNotification } = useNotification();
  const { saveHandlers } = useDefaultCRUDHandlers("Lead");
  const { setInfo } = usePageNameContext();
  const { createMultiple } = useLeads(true, "CreateLeadMultipleVehicles");
  const { vrmLookup } = useVehiclesMutations();

  const [selectedContact, setSelectedContact] = useState<IContact | null>(null);
  const [address, setAddress] = useState<any>();
  const [postcode, setPostcode] = useState("");
  const [addressesOpen, setAddressesOpen] = useState(false);
  const [previousVehicle, setPreviousVehicle] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState<DefaultResponseWithData<ILead>[]>([]);
  const [resultsOpen, setResultsOpen] = useState(false);

  const [createVehicleOpen, setCreateVehicleOpen] = useState(false);

  const { register, getValues, setValue, watch, handleSubmit, reset, control } = useForm<any>({
    defaultValues: {
      isDropOff: false,
      commission: 0,
      price: 0,
      website: LeadSource.Manual,
      vehicles: [],
    },
  });

  const sourceOptions = useMemo(() => {
    return Object.keys(LeadSource)
      .filter(key => isNaN(Number(key)))
      .map(key => {
        const value = LeadSource[key as keyof typeof LeadSource] as number;
        return {
          id: value,
          name: t(key),
        };
      });
  }, [t]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "vehicles",
  });

  const [isSearching, setIsSearching] = useState(fields.map(() => false));

  useEffect(() => {
    setIsSearching((prev) => {
      if (fields.length === prev.length) return prev;
      if (fields.length > prev.length) return [...prev, false];
      return prev.slice(0, fields.length);
    });
  }, [fields.length]);

  const handleVrmLookup = (index: number, vrm?: string) => {
    setIsSearching((prev) => prev.map((v, i) => (i === index ? true : v)));
    vrm ??= getValues(`vehicles.${index}.vrm`);
    vrmLookup.mutate(vrm ?? "", {
      onSuccess: (data: any) => {
        if (vrm !== undefined) setValue(`vehicles.${index}.vrm`, vrm);
        setValue(`vehicles.${index}.make`, data.data.combinedMake);
        setValue(`vehicles.${index}.model`, data.data.combinedModel);
        addNotification({
          variant: "success",
          primaryText: t("Found vehicle"),
        });

        if (DateTime.fromISO(data.data.created) < DateTime.now().minus({ minutes: 1 })) {
          setPreviousVehicle(true);
        }

        setIsSearching((prev) => prev.map((v, i) => (i === index ? false : v)));
      },
      onError: (error) => {
        addNotification({
          variant: "error",
          primaryText: t("Error looking up VRM"),
        });
        setIsSearching((prev) => prev.map((v, i) => (i === index ? false : v)));
      },
    });
  };

  const handleOnContactSelected = (contact: IContact | null) => {
    const data = getValues();

    if (!contact) {

      //Reset everything but the vehicles
      reset({
        vehicles: data.vehicles,
      });
      setSelectedContact(null);
      return;
    }

    setSelectedContact(contact);

    reset({
      ...data,
      ...contact,
    });
  }

  const onSubmit = (data: any) => {

    if (!selectedContact) {
      addNotification({
        variant: "error",
        primaryText: t("Please select a contact before creating a lead."),
      });
      setIsLoading(false);
      return;
    }

    if (data.vehicles.length === 0) {
      addNotification({
        variant: "error",
        primaryText: t("Please add at least one vehicle before creating a lead."),
      });
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    let newLead = {
      ...data,
      contact: {
        ...selectedContact,
        contactId: selectedContact.id || "00000000-0000-0000-0000-000000000000",
      },
      vehicles: data.vehicles.map((vehicle: any) => ({
        ...vehicle,
        ...vehicle.address,
      }) as IVehicle)
    };

    newLead.vehicles.forEach((vehicle: IVehicle) => {
      if (vehicle.isDropOff && vehicle.alreadyOnSite) {
        vehicle.collectedOn = new Date().toISOString();
      }
    });

    createMultiple.mutate(newLead, {
      onSuccess: (data) => {
        // saveHandlers.onSuccess();
        setResults(data);
        setResultsOpen(true);
      },
      onError: (error) => {
        saveHandlers.onError();
      },
      onSettled: () => setIsLoading(false),
    });
  };

  useEffect(() => {
    setInfo({
      name: isBookingPath ? t("New Booking") : t("New Lead"),
      desc: isBookingPath ? t("Create new manual bookings") : t("Create new manual leads"),
    });
  }, [leadLabel]);

  useEffect(() => {
    //For closing at the end
    if (!resultsOpen && results.length > 0) {
      navigate(isBookingPath ? "/bookings" : "/leads");
    }
  }, [resultsOpen, results.length, navigate, isBookingPath]);

  const pages = [
    { to: "..", label: t(leadListLabel), current: false },
    {
      to: "",
      label: isBookingPath ? t("New Booking") : t("New Lead"),
      current: true,
    },
  ];

  if (isLoading) {
    return (
      <div className="pt-[20vh] flex justify-center items-center h-full">
        <LoadingWheel />
      </div>
    );
  } else {
    return (
      <>
        <Modal open={resultsOpen} width={"max-w-3xl"} setOpen={setResultsOpen}>
          <div className="p-4 flex flex-col gap-4">
            <h1 className="text-lg font-semibold">{isBookingPath ? t("Bookings Created") : t("Leads Created")}</h1>
            {!results.length || results.length === 0 ? (
              <p className="text-sm text-gray-500">{isBookingPath ? t("No bookings created.") : t("No leads created.")}</p>
            ) : (
              <ul className="list-disc pl-5">
                {results.map((result) => (
                  <li key={result.data.id} className="flex items-center gap-2">
                    <span className="text-sm font-medium">{result.success ? "✅" : "❌"}</span>

                    {result.success && (
                      <>
                        {result.data.vehicle?.vehicleImageUrl ? (
                          <img
                            className="w-32 h-20 object-contain"
                            src={`${result.data.vehicle?.vehicleImageUrl}`}
                          />
                        ) : (
                          <CameraIcon className="w-14 text-gray-600" />
                        )}
                        <span>{result.data.vehicle?.vrm}</span>
                        <span>{result.data.vehicle?.combinedMake}</span>
                        <span>{result.data.vehicle?.modelRangeDescription}</span>
                        <span>-</span>
                      </>
                    )}

                    <span>{result.message}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Modal>
        <Modal open={previousVehicle} setOpen={setPreviousVehicle} width="max-w-xl">
          <p>{t("A vehicle with this VRM already exists in the system.")} </p>
          <br />
          <p>{isBookingPath
            ? t("If you proceed, a new booking will be created which may lead to duplicates in the system.")
            : t("If you proceed, a new lead will be created which may lead to duplicates in the system.")}</p>
          <div className="mt-4 flex justify-end">
            <ButtonInput label={t("OK")} isSubmit={false} classes="ml-2" onClick={() => setPreviousVehicle(false)} />
          </div>
        </Modal>
        <GetAddresses
          postcode={postcode}
          setAddress={setAddress}
          open={addressesOpen}
          setOpen={setAddressesOpen}
        />
        <Breadcrumbs pages={pages} />

        <Card containerClassName="mt-4">
          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-8 p-4 px-6">
            <div className="">
              <h1 className="text-lg font-semibold">{t("Contact Details")}</h1>

              <ContactSelector
                onContactSelected={handleOnContactSelected}
                initialContact={selectedContact}
              />

              {selectedContact?.account && selectedContact.account.accountType === AccountType.Manual && (
                <div className="mt-4 w-fit">
                  <h2 className="text-sm font-semibold mb-2">{t("Selected Account")}</h2>
                  <div className="p-4 bg-gray-100 rounded-md">
                    <p className="text-sm font-medium">{selectedContact.account.name}</p>
                    <p className="text-sm font-medium">{selectedContact.account.bankSortCode} / {selectedContact.account.bankAccountNumber}</p>
                  </div>
                </div>
              )}
            </div>

            <div>
              <SelectInput
                label={t("Source")}
                register={register}
                registerName="website"
                options={sourceOptions}
                setValue={setValue} watch={watch}
              />
            </div>

            {/* Vehicle rows section */}
            <div className="">
              <h1 className="text-lg font-semibold mb-2">{t("Vehicles")}</h1>
              <LeadVehiclesTable
                vehicles={fields}
                register={register}
                setValue={setValue}
                getValues={getValues}
                watch={watch}
                remove={remove}
                handleVrmLookup={handleVrmLookup}
                isSearching={isSearching}
                createVehicleOpen={createVehicleOpen}
                setCreateVehicleOpen={setCreateVehicleOpen}
              />
              <ButtonInput
                label={t("Add Vehicle")}
                onClick={(e: React.MouseEvent) => {
                  e.preventDefault();
                  append({
                    vrm: "",
                    make: "",
                    model: "",
                    applyVAT: false,
                    address: selectedContact ? {
                      line1: selectedContact.line1 || "",
                      line2: selectedContact.line2 || "",
                      line3: selectedContact.line3 || "",
                      line4: selectedContact.line4 || "",
                      locality: selectedContact.locality || "",
                      town: selectedContact.town || "",
                      county: selectedContact.county || "",
                      postcode: selectedContact.postcode || "",
                    } : {}
                  });
                  setCreateVehicleOpen(true);
                }}
                classes="mt-2"
              />
            </div>

            <div className="mt-6 flex justify-end">
              <ButtonInput
                label={isBookingPath ? t("Create Booking") : t("Create Lead")}
                isSubmit
                classes=""
                onClick={null}
              />
            </div>

          </form>
        </Card>
      </>
    );
  }
}
